import { initComponentStore, noop } from "@/utils/mobx";
import { VirtualKeyboardStore } from "@/components/toolkit/VirtualKeyboard/store";


export class TextInputStore {
    value: string = "";
    onChange: (value: string) => any = noop;
    onEnter: ((value: string) => any) | null = null;
    isTextArea: boolean = false;
    submitOnEnter: boolean = false;
    placeholder: string = "";
    autoFocus: boolean = false;
    password: boolean = false;
    selectOnFocus: boolean = false;

    // Virtual keyboard state
    vkbCursorPos: number = 0;
    // Highlighted selection range for the on-screen overlay. When start === end
    // there is no selection and only the caret (at vkbCursorPos) is shown.
    vkbSelStart: number = 0;
    vkbSelEnd: number = 0;
    vkbVisible: boolean = false;
    vkbShowEmbedded: boolean = false;
    vkbStore: VirtualKeyboardStore = new VirtualKeyboardStore();
    // Tracks the current selection range for text insertion/deletion.
    // Not observable — MobX doesn't need to track raw selection bookkeeping.
    _cursor: { start: number; end: number } = { start: 0, end: 0 };
    _vkbHistoryPushed: boolean = false;
    _vkbIgnoreNextPopState: boolean = false;
    _popstateHandler: (() => void) | null = null;

    constructor() {
        initComponentStore(this, { _cursor: false, _vkbHistoryPushed: false, _vkbIgnoreNextPopState: false, _popstateHandler: false, vkbStore: false });
    }

    updateValue(value: string) {
        this.value = value;
        this.onChange(this.value);
    }

    setCursor(pos: number) {
        this._cursor = { start: pos, end: pos };
        this.vkbCursorPos = pos;
        this.vkbSelStart = pos;
        this.vkbSelEnd = pos;
    }

    setSelectionRange(start: number, end: number) {
        this._cursor = { start, end };
        // Caret trails the selection so the highlight is visible and typing replaces it.
        this.vkbCursorPos = end;
        this.vkbSelStart = start;
        this.vkbSelEnd = end;
    }

    toggleShowEmbedded(value: boolean) {
        this.vkbShowEmbedded = value;
    }

    openKeyboard(showEmbedded: boolean) {
        this.vkbShowEmbedded = showEmbedded;
        this.vkbVisible = true;
        history.pushState({ vkb: true }, "");
        this._vkbHistoryPushed = true;
        this._vkbIgnoreNextPopState = false;
        const handler = () => {
            if (this._vkbIgnoreNextPopState) {
                this._vkbIgnoreNextPopState = false;
                return;
            }
            // If splitter is enabled and the keyboard is expanded, collapse first.
            if (this.vkbStore.enableSplitter && !this.vkbStore.collapsed) {
                this.vkbStore.setCollapsed(true);
                history.pushState({ vkb: true }, "");
                this._vkbHistoryPushed = true;
                return;
            }
            this._vkbHistoryPushed = false;
            this.closeKeyboard();
        };
        this._popstateHandler = handler;
        window.addEventListener("popstate", handler);
    }

    closeKeyboard() {
        this.vkbVisible = false;
        const handler = this._popstateHandler;
        this._popstateHandler = null;
        if (handler) {
            window.removeEventListener("popstate", handler);
        }
        if (this._vkbHistoryPushed) {
            this._vkbHistoryPushed = false;
            // Convert the vkb history entry back to a guard in-place.
            // Using replaceState avoids an extra back-navigation that would
            // deplete the PWA guard stack maintained in index.tsx.
            history.replaceState({ pwa_guard: true }, "");
        }
    }

    dispose() {
        this.closeKeyboard();
        this.vkbStore.dispose();
    }

    insertText(text: string) {
        const { start, end } = this._cursor;
        const newValue = this.value.slice(0, start) + text + this.value.slice(end);
        const newCursor = start + text.length;
        this.setCursor(newCursor);
        this.updateValue(newValue);
    }

    handleVkbKey(key: string) {
        if (key === "Enter") {
            if (this.onEnter) {
                this.onEnter(this.value);
            } else if (this.isTextArea) {
                this.insertText("\n");
            }
            return;
        }
        if (key === "Backspace") {
            this.deleteBack();
        } else {
            this.insertText(key);
        }
    }

    deleteBack() {
        const { start, end } = this._cursor;
        if (start !== end) {
            this.setCursor(start);
            this.updateValue(this.value.slice(0, start) + this.value.slice(end));
        } else if (start > 0) {
            this.setCursor(start - 1);
            this.updateValue(this.value.slice(0, start - 1) + this.value.slice(start));
        }
    }
}
